"use client"

import { useRef } from "react"
import { getTotalDuration } from "../../../lib/animation-engine"
import type { AnimationPreset } from "./take-types"

interface TimelineProps {
  preset: AnimationPreset
  currentTime: number
  selectedId: string
  onScrub: (t: number) => void
  onSelect: (id: string) => void
}

const TAKE_COLORS = [
  "bg-chart-1",
  "bg-chart-2",
  "bg-chart-3",
  "bg-chart-4",
  "bg-chart-5",
]

export function Timeline({ preset, currentTime, selectedId, onScrub, onSelect }: TimelineProps) {
  const total = getTotalDuration(preset)
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  const timeFromEvent = (clientX: number) => {
    const el = trackRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    return ratio * total
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    onScrub(timeFromEvent(e.clientX))
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    onScrub(timeFromEvent(e.clientX))
  }

  const handlePointerUp = () => {
    draggingRef.current = false
  }

  // Marcas de segundo na régua
  const ticks = Array.from({ length: Math.floor(total) + 1 }, (_, i) => i)

  return (
    <div className="select-none">
      {/* Régua de tempo */}
      <div className="relative mb-1 h-4">
        {ticks.map((t) =>
          t % 2 === 0 ? (
            <span
              key={t}
              className="absolute top-0 -translate-x-1/2 font-mono text-[9px] text-muted-foreground"
              style={{ left: `${(t / total) * 100}%` }}
            >
              {t}s
            </span>
          ) : null,
        )}
      </div>

      {/* Trilha com blocos de takes */}
      <div
        ref={trackRef}
        className="relative h-12 w-full cursor-pointer rounded-md bg-muted"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {preset.takes.map((take, i) => {
          const left = (take.startTime / total) * 100
          const width = ((take.endTime - take.startTime) / total) * 100
          const selected = take.id === selectedId
          return (
            <button
              key={take.id}
              onPointerDown={(e) => {
                e.stopPropagation()
                onSelect(take.id)
                onScrub(timeFromEvent(e.clientX))
                draggingRef.current = true
              }}
              className={`absolute top-1 bottom-1 overflow-hidden rounded px-2 text-left text-[10px] font-medium text-white/90 transition-all ${
                TAKE_COLORS[i % TAKE_COLORS.length]
              } ${selected ? "ring-2 ring-foreground ring-offset-1 ring-offset-muted" : "opacity-80 hover:opacity-100"}`}
              style={{ left: `${left}%`, width: `${width}%` }}
              title={take.name}
            >
              <span className="block truncate leading-tight">{take.name}</span>
              {take.fadeIn && <span className="block text-[8px] opacity-70">fade</span>}
            </button>
          )
        })}

        {/* Playhead */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 w-0.5 bg-foreground"
          style={{ left: `${(currentTime / total) * 100}%` }}
        >
          <div className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-foreground" />
        </div>
      </div>
    </div>
  )
}
